import buildHeaders from '../../../../utils/api/fetchHeaders';

export const AI_ASSISTANT_CHAT_API_BASE = process.env.REACT_APP_AI_ASSISTANT_CHAT_API_BASE || 'http://localhost:6767';

function resolveApiBaseUrl(apiBaseUrl) {
    return apiBaseUrl || AI_ASSISTANT_CHAT_API_BASE;
}

async function parseResponseBody(response) {
    const text = await response.text();
    if (!text) {
        return null;
    }

    try {
        return JSON.parse(text);
    } catch (_) {
        return text;
    }
}

function emitDebugEvent(onDebugEvent, event) {
    if (typeof onDebugEvent === 'function') {
        onDebugEvent(event);
    }
}

function buildErrorMessage(fallbackMessage, payload) {
    if (typeof payload === 'string' && payload.trim()) {
        return `${fallbackMessage}: ${payload}`;
    }

    const apiMessage = payload?.error?.message || payload?.message;
    return apiMessage ? `${fallbackMessage}: ${apiMessage}` : fallbackMessage;
}

async function requestJson(path, options = {}, fallbackMessage = 'Request failed', apiBaseUrl, onDebugEvent) {
    const response = await fetch(`${resolveApiBaseUrl(apiBaseUrl)}${path}`, {
        ...options,
        headers: buildHeaders({
            Accept: 'application/json',
            ...(options.headers || {}),
        }),
    });

    const payload = await parseResponseBody(response);
    emitDebugEvent(onDebugEvent, {
        source: 'http',
        path,
        method: options.method || 'GET',
        status: response.status,
        ok: response.ok,
        requestBody: options.body || null,
        payload,
    });

    if (!response.ok) {
        throw new Error(buildErrorMessage(fallbackMessage, payload));
    }

    return payload;
}

export function buildConversationStreamUrl(conversationId, apiBaseUrl) {
    return `${resolveApiBaseUrl(apiBaseUrl)}/api/conversations/${encodeURIComponent(conversationId)}/stream`;
}

export function buildRunStreamUrl(runId, apiBaseUrl, format = 'events') {
    const query = format ? `?format=${encodeURIComponent(format)}` : '';
    return `${resolveApiBaseUrl(apiBaseUrl)}/api/runs/${encodeURIComponent(runId)}/stream${query}`;
}

export async function checkChatHealth(apiBaseUrl, onDebugEvent) {
    return requestJson('/health', {method: 'GET'}, 'Failed to reach AI Assistant Chat backend', apiBaseUrl, onDebugEvent);
}

export async function listConversations(apiBaseUrl, onDebugEvent) {
    return requestJson('/api/conversations', {method: 'GET'}, 'Failed to list conversations', apiBaseUrl, onDebugEvent);
}

export async function createConversation({documentId, documentIds, documentType, documentTitle, apiBaseUrl, onDebugEvent} = {}) {
    const resolvedDocumentIds = documentIds && documentIds.length > 0
        ? documentIds
        : (documentId ? [documentId] : []);
    const payload = {
        metadata: {
            source: 'ui',
            reportId: documentId || null,
            documentIds: resolvedDocumentIds,
            documentType: documentType || 'report',
            documentTitle: documentTitle || '',
        },
    };

    if (documentTitle) {
        payload.title = documentTitle;
    }

    return requestJson('/api/conversations', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    }, 'Failed to create chat conversation', apiBaseUrl, onDebugEvent);
}

export async function getConversation(conversationId, apiBaseUrl, onDebugEvent) {
    return requestJson(`/api/conversations/${encodeURIComponent(conversationId)}`, {
        method: 'GET',
    }, 'Failed to load chat conversation', apiBaseUrl, onDebugEvent);
}

export async function deleteConversation(conversationId, apiBaseUrl, onDebugEvent) {
    const response = await fetch(
        `${resolveApiBaseUrl(apiBaseUrl)}/api/conversations/${encodeURIComponent(conversationId)}`,
        {
            method: 'DELETE',
            headers: buildHeaders({}),
        },
    );
    emitDebugEvent(onDebugEvent, {
        source: 'http',
        path: `/api/conversations/${conversationId}`,
        method: 'DELETE',
        status: response.status,
        ok: response.ok,
        requestBody: null,
        payload: null,
    });
    if (!response.ok && response.status !== 204) {
        const payload = await parseResponseBody(response);
        throw new Error(buildErrorMessage('Failed to delete conversation', payload));
    }
}

export async function sendMessage(conversationId, message, apiBaseUrl, onDebugEvent, metadata = null) {
    const payload = {message};
    if (metadata && Object.keys(metadata).length > 0) {
        // Ensure documentIds is always an array when present
        const normalised = {...metadata};
        if (normalised.documentIds === undefined && normalised.docId) {
            normalised.documentIds = [normalised.docId];
        }
        payload.metadata = normalised;
    }

    return requestJson(`/api/conversations/${encodeURIComponent(conversationId)}/messages`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    }, 'Failed to send chat message', apiBaseUrl, onDebugEvent);
}

